<template>
    <div
        v-show="bannerVisible"
        :class="cookieModal ? 'bg-transparent' : homepage ? 'bg-gray-900/100' : 'bg-primary/90'"
        class="cookie-consent-banner fixed bottom-0 lg:bottom-2 p-5 text-sm shadow-md left-0 max-w-4xl right-0 lg:rounded-lg text-left mx-auto z-[100]"
        ref="banner"
    >
        <div v-if="!cookieModal">
            <h4
                :class="homepage ? 'text-white ' : 'text-black font-peanut'"
                class="text-lg tracking-normal font-bold mb-2 md:mb-0"
            ></h4>
            <div class="lg:flex items-center justify-between space-x-1">
                <div class="md:flex flex-col mt-1 md:space-x-1">
                    <p class="text-white font-bold">
                        {{ $t('appcookie.banner_desc') }}
                    </p>
                    <p class="font-bold mt-1 md:mt-0 text-white">
                        {{ $t('appcookie.banner_click_here') }}
                        <span @click="cookieModal = true" class="underline cursor-pointer">{{
                            $t('appcookie.banner_manage_options')
                        }}</span>
                    </p>
                </div>
                <div class="md:flex-row flex flex-col space-y-3 md:space-x-2 md:space-y-0 mt-4 md:mt-0 justify-center">
                    <template v-if="!homepage">
                        <AppButton class="font-bold bg-primary rounded-md px-4 py-2" @click="acceptCookies(true)">
                            {{ $t('appcookie.accept_all') }}
                        </AppButton>
                        <AppButton
                            is-secondary
                            class="bg-secondary px-4 py-2 font-bold text-white rounded-md"
                            @click="declineAll"
                        >
                            {{ $t('appcookie.reject_all') }}
                        </AppButton>
                    </template>
                    <div class="flex space-x-4 mt-4 md:mt-8 lg:mt-0" v-else>
                        <div :class="homepage && 'cta-button'" class="cursor-pointer">
                            <a class="font-bold bg-primary rounded-md px-4 py-2" @click="acceptCookies(true)">
                                {{ $t('appcookie.accept_all') }}
                            </a>
                        </div>
                        <div
                            :class="homepage && 'cta-button-secondary'"
                            class="cursor-pointer text-white/90 hover:text-white"
                        >
                            <a is-secondary class="bg-secondary font-bold px-4 py-2 rounded-md" @click="declineAll">
                                {{ $t('appcookie.reject_all') }}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <PrimeDialog
            :pt="{
                root: {
                    style: {
                        backgroundColor: homepage ? 'black' : 'white',
                        minWidth: '25rem',
                    },
                },
                content: {
                    style: {
                        backgroundColor: homepage ? 'black' : 'white',
                        minWidth: '25rem',
                    },
                },

                header: {
                    style: {
                        fontWeight: '800',
                        font: homepage ? 'inherit' : 'font-peanut',
                        backgroundColor: homepage ? 'black' : 'white',
                        minWidth: '25rem',
                    },
                },
            }"
            modal
            :header="$t('appcookie.privacy_settings')"
            v-model:visible="cookieModal"
            :title="$t('appcookie.detailed_settings')"
        >
            <div class="max-h-[310px] max-w-xs md:max-w-full">
                <div v-for="item in cookieOptions" :key="item.title" class="bg-light text-white py-2 mb-3">
                    <div class="flex justify-between">
                        <div @click="item.opened = !item.opened">
                            <div class="flex space-x-2 items-center">
                                <h4 class="text-primary font-bold tex-sm">{{ $t(item.title) }}</h4>
                            </div>
                            <div>
                                <p :class="homepage ? 'text-white' : 'text-black'" class="text-xs">
                                    {{ $t(item.description) }}
                                </p>
                            </div>
                        </div>
                        <div class="w-[40px]">
                            <AppSwitch v-model="item.selected" :disabled="item.disabled"></AppSwitch>
                        </div>
                    </div>
                </div>
            </div>

            <div
                class="md:flex-row max-w-xs md:max-w-full flex flex-col space-y-3 md:space-y-0 items-center mt-4 md:justify-between"
            >
                <div v-if="!homepage" class="md:flex w-full items-center md:w-auto md:space-x-3 space-y-3 md:space-y-0">
                    <AppButton
                        class="font-bold px-4 py-2 bg-primary rounded-md w-full md:w-auto text-black"
                        @click="acceptCookies(true)"
                    >
                        {{ $t('appcookie.accept_all') }}
                    </AppButton>
                    <AppButton
                        is-secondary
                        class="w-full bg-secondary rounded-md cursor-pointer text-white px-4 py-2 md:w-fit"
                        @click="saveChoices"
                    >
                        {{ $t('appcookie.save_choices') }}
                    </AppButton>
                </div>

                <div v-else class="flex w-full items-center md:w-auto md:space-x-3 md:space-y-0">
                    <div class="cta-button cursor-pointer">
                        <a
                            class="font-bold px-4 text-sm py-2 bg-primary rounded-md w-full md:w-auto text-black"
                            @click="acceptCookies(true)"
                        >
                            {{ $t('appcookie.accept_all') }}
                        </a>
                    </div>
                    <div class="cta-button-secondary text-sm cursor-pointer">
                        <a
                            class="w-full bg-secondary rounded-md font-bold text-white px-4 py-2 md:w-fit"
                            @click="saveChoices"
                        >
                            {{ $t('appcookie.save_choices') }}
                        </a>
                    </div>
                </div>
            </div>
        </PrimeDialog>
    </div>
</template>

<script setup lang="ts">
    import gsap from 'gsap';
    import { onMounted, ref } from 'vue';

    const { gtag } = useGtag();

    defineProps<{
        homepage?: boolean;
    }>();

    const setCookie = (name, value, days) => {
        const expires = new Date(Date.now() + days * 864e5).toUTCString();
        document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
    };

    const getCookie = (name) => {
        return document.cookie
            .split('; ')
            .find((row) => row.startsWith(name))
            ?.split('=')[1];
    };

    const bannerVisible = defineModel(false);
    const cookieModal = ref(false);
    const banner = ref(null);
    const consentGiven = ref(false);

    const cookieOptions = ref([
        {
            type: 'necessary',
            title: 'appcookie.necessary_title',
            selected: true,
            disabled: true,
            opened: false,
            description: 'appcookie.necessary_desc',
        },
        {
            type: 'analytics',
            title: 'appcookie.analytics_title',
            selected: false,
            opened: false,
            description: 'appcookie.analytics_desc',
        },
        {
            type: 'marketing',
            title: 'appcookie.marketing_title',
            selected: false,
            opened: false,
            description: 'appcookie.marketing_desc',
        },
    ]);

    const showBanner = () => {
        gsap.to(banner.value, { y: 0, opacity: 1, duration: 0.5 });
    };

    const hideBanner = async () => {
        await gsap.to(banner.value, { y: '100%', opacity: 1, duration: 0.5 });
        bannerVisible.value = false;
    };

    watch(
        () => bannerVisible.value,
        () => {
            if (bannerVisible.value) gsap.to(banner.value, { y: 0, opacity: 1, duration: 0.5 });
        },
    );

    const saveChoices = () => {
        cookieOptions.value.forEach((option) => {
            setCookie(`${option.type}_consent`, option.selected, 30);
        });
        updateConsentState();
        hideBanner();
        cookieModal.value = false;
        consentGiven.value = true;
    };

    const acceptCookies = (acceptAll) => {
        if (acceptAll) {
            cookieOptions.value.forEach((option) => {
                if (!option.disabled) option.selected = true;
            });
        }
        saveChoices();
    };

    const declineAll = () => {
        cookieOptions.value.forEach((option) => {
            if (!option.disabled) option.selected = false;
        });
        saveChoices();
    };

    const updateConsentState = () => {
        const analyticsEnabled = cookieOptions.value.find((opt) => opt.type === 'analytics').selected;
        const marketingEnabled = cookieOptions.value.find((opt) => opt.type === 'marketing').selected;

        const consentOptions = {
            ad_storage: marketingEnabled ? 'granted' : 'denied',
            analytics_storage: analyticsEnabled ? 'granted' : 'denied',
        };

        if (typeof gtag === 'function') {
            gtag('consent', 'update', consentOptions);
        }
    };

    onMounted(() => {
        cookieOptions.value.forEach((option) => {
            const consent = getCookie(`${option.type}_consent`);
            if (!option.disabled) option.selected = consent === 'true';
        });

        const analyticsConsent = getCookie('analytics_consent');
        const marketingConsent = getCookie('marketing_consent');

        if (!analyticsConsent && !marketingConsent) {
            setTimeout(() => {
                bannerVisible.value = true;
                showBanner();
            }, 500);
        } else {
            updateConsentState();
            consentGiven.value = true;
        }
    });
</script>

<style scoped>
    .cookie-consent-banner {
        transition: opacity 0.5s;
        transform: translateY(100%);
        opacity: 1;
    }
</style>
